import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    loadManagedAppointment,
    cancelAppointment,
    getManageAvailability,
    getManageSlots,
    rescheduleAppointment,
    ManagedAppointment,
    ManagePolicy,
    ManageDay,
    ManageSlot,
    SelfserviceError,
} from '../services/selfserviceApi';
import { getManageStrings, manageDateLocale } from '../translations/manage';

// Patientenseite „Termin verwalten" (/termin/:token): ansehen, absagen,
// verschieben. Token-basiert wie die Intake-Seite — kein Login.

type View = 'details' | 'confirmCancel' | 'reschedule' | 'cancelled' | 'rescheduled';

type LoadState =
    | { status: 'loading' }
    | { status: 'error'; message: string }
    | { status: 'ready' };

const RANGE_DAYS = 28; // Umbuchungs-Horizont für die Tagesauswahl

const formatDateTime = (a: ManagedAppointment): string => {
    const locale = manageDateLocale(a.language);
    const d = new Date(`${a.appointment_date}T${a.start_time}`);
    return `${d.toLocaleDateString(locale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}, ${a.start_time}`;
};

const formatDay = (dateStr: string, language: string): string => {
    const locale = manageDateLocale(language);
    return new Date(`${dateStr}T12:00`).toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });
};

const isoDatePlus = (days: number): string => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
};

const ManageAppointmentPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [load, setLoad] = useState<LoadState>({ status: 'loading' });
    const [appointment, setAppointment] = useState<ManagedAppointment | null>(null);
    const [policy, setPolicy] = useState<ManagePolicy | null>(null);
    const [view, setView] = useState<View>('details');
    const [busy, setBusy] = useState(false);
    const [flash, setFlash] = useState<string | null>(null);

    // Umbuchung
    const [days, setDays] = useState<ManageDay[] | null>(null);
    const [selectedDay, setSelectedDay] = useState<string | null>(null);
    const [slots, setSlots] = useState<ManageSlot[] | null>(null);
    const [selectedSlot, setSelectedSlot] = useState<ManageSlot | null>(null);

    const strings = getManageStrings(appointment?.language);

    useEffect(() => {
        if (!token) {
            setLoad({ status: 'error', message: 'Missing token' });
            return;
        }
        let cancelled = false;
        loadManagedAppointment(token)
            .then((data) => {
                if (cancelled) return;
                document.documentElement.lang = data.appointment.language || 'de';
                document.title = getManageStrings(data.appointment.language).pageTitle;
                setAppointment(data.appointment);
                setPolicy(data.policy);
                setLoad({ status: 'ready' });
            })
            .catch((err) => {
                if (cancelled) return;
                setLoad({ status: 'error', message: err?.message || 'error' });
            });
        return () => { cancelled = true; };
    }, [token]);

    const openReschedule = useCallback(async () => {
        if (!token) return;
        setView('reschedule');
        setFlash(null);
        setDays(null);
        setSelectedDay(null);
        setSlots(null);
        setSelectedSlot(null);
        try {
            const result = await getManageAvailability(token, isoDatePlus(0), isoDatePlus(RANGE_DAYS));
            setDays(result);
        } catch {
            setDays([]);
        }
    }, [token]);

    const pickDay = useCallback(async (date: string) => {
        if (!token) return;
        setSelectedDay(date);
        setSlots(null);
        setSelectedSlot(null);
        try {
            setSlots(await getManageSlots(token, date));
        } catch {
            setSlots([]);
        }
    }, [token]);

    const doCancel = useCallback(async () => {
        if (!token) return;
        setBusy(true);
        setFlash(null);
        try {
            const result = await cancelAppointment(token);
            setAppointment(result.appointment);
            setView('cancelled');
        } catch (err) {
            setFlash(err instanceof SelfserviceError && err.status !== 500 ? strings.notActionable : strings.errorGeneric);
            setView('details');
        } finally {
            setBusy(false);
        }
    }, [token, strings]);

    const doReschedule = useCallback(async () => {
        if (!token || !selectedDay || !selectedSlot) return;
        setBusy(true);
        setFlash(null);
        try {
            const result = await rescheduleAppointment(token, selectedDay, selectedSlot.start_time);
            setAppointment(result.appointment);
            setPolicy(result.policy);
            setView('rescheduled');
        } catch (err) {
            if (err instanceof SelfserviceError && err.code === 'slot_taken') {
                setFlash(strings.rescheduleFlow.slotTaken);
                // Slot-Liste auffrischen — das Rennen ist verloren, der Tag bleibt gewählt.
                if (selectedDay) pickDay(selectedDay);
            } else {
                setFlash(strings.errorGeneric);
                setView('details');
            }
        } finally {
            setBusy(false);
        }
    }, [token, selectedDay, selectedSlot, strings, pickDay]);

    const availableDays = useMemo(() => (days || []).filter(d => d.hasSlots), [days]);

    if (load.status === 'loading') {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-[#f9f9f7] to-white px-6">
                <div className="h-12 w-12 rounded-full border-4 border-[#384860]/20 border-t-[#384860] animate-spin" />
                <p className="mt-4 text-[#384860]/70 text-sm">{strings.loading}</p>
            </div>
        );
    }

    if (load.status === 'error' || !appointment || !policy) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-[#f9f9f7] to-white px-6 text-center">
                <div className="max-w-md">
                    <div className="mx-auto h-16 w-16 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                    </div>
                    <h1 className="text-xl font-semibold text-[#384860]">{strings.errorLink}</h1>
                </div>
            </div>
        );
    }

    const statusLabel = strings.status[appointment.status] || appointment.status;

    const detailsCard = (
        <div className="bg-white rounded-2xl shadow-lg ring-1 ring-[#384860]/10 p-6 text-left">
            <dl className="space-y-3">
                <div>
                    <dt className="text-xs uppercase tracking-wide text-[#384860]/50">{strings.labels.treatment}</dt>
                    <dd className="text-[#384860] font-medium">{appointment.procedure_name}</dd>
                </div>
                <div>
                    <dt className="text-xs uppercase tracking-wide text-[#384860]/50">{strings.labels.dateTime}</dt>
                    <dd className="text-[#384860] font-medium">{formatDateTime(appointment)}</dd>
                </div>
                <div>
                    <dt className="text-xs uppercase tracking-wide text-[#384860]/50">{strings.labels.dentist}</dt>
                    <dd className="text-[#384860] font-medium">{appointment.provider_name}</dd>
                </div>
                <div>
                    <dt className="text-xs uppercase tracking-wide text-[#384860]/50">{strings.labels.status}</dt>
                    <dd className="text-[#384860] font-medium">{statusLabel}</dd>
                </div>
            </dl>
        </div>
    );

    const renderBody = () => {
        if (view === 'cancelled' || view === 'rescheduled') {
            const success = view === 'cancelled' ? strings.cancelFlow : strings.rescheduleFlow;
            return (
                <>
                    <div className="mx-auto h-16 w-16 rounded-2xl bg-[#93cbb6]/20 flex items-center justify-center mb-4">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2f7d5f" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                    </div>
                    <h1 className="text-2xl font-semibold text-[#384860]">{success.successTitle}</h1>
                    <p className="mt-3 mb-6 text-[#384860]/70">{success.successBody}</p>
                    {view === 'rescheduled' && detailsCard}
                </>
            );
        }

        if (view === 'confirmCancel') {
            return (
                <>
                    <h1 className="text-2xl font-semibold text-[#384860]">{strings.cancelFlow.title}</h1>
                    <p className="mt-3 mb-6 text-[#384860]/70">{strings.cancelFlow.body}</p>
                    {detailsCard}
                    <div className="mt-6 flex flex-col gap-3">
                        <button
                            onClick={doCancel}
                            disabled={busy}
                            className="w-full rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-semibold py-3 transition-colors"
                        >
                            {strings.cancelFlow.confirm}
                        </button>
                        <button
                            onClick={() => setView('details')}
                            disabled={busy}
                            className="w-full rounded-xl border border-[#384860]/20 text-[#384860] font-semibold py-3 hover:bg-[#384860]/5 transition-colors"
                        >
                            {strings.cancelFlow.keep}
                        </button>
                    </div>
                </>
            );
        }

        if (view === 'reschedule') {
            return (
                <>
                    <h1 className="text-2xl font-semibold text-[#384860]">{strings.rescheduleFlow.title}</h1>
                    {flash && <p className="mt-3 text-sm text-red-600">{flash}</p>}

                    <p className="mt-5 mb-2 text-sm font-medium text-[#384860]/70 text-left">{strings.rescheduleFlow.pickDay}</p>
                    {days === null ? (
                        <div className="h-8 w-8 mx-auto rounded-full border-4 border-[#384860]/20 border-t-[#384860] animate-spin" />
                    ) : availableDays.length === 0 ? (
                        <p className="text-[#384860]/70 text-sm">{strings.rescheduleFlow.noSlots}</p>
                    ) : (
                        <div className="flex flex-wrap gap-2 justify-start">
                            {availableDays.map(d => (
                                <button
                                    key={d.date}
                                    onClick={() => pickDay(d.date)}
                                    className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${selectedDay === d.date
                                        ? 'bg-[#384860] text-white border-[#384860]'
                                        : 'bg-white text-[#384860] border-[#384860]/20 hover:border-[#384860]/60'}`}
                                >
                                    {formatDay(d.date, appointment.language)}
                                </button>
                            ))}
                        </div>
                    )}

                    {selectedDay && (
                        <>
                            <p className="mt-6 mb-2 text-sm font-medium text-[#384860]/70 text-left">{strings.rescheduleFlow.pickTime}</p>
                            {slots === null ? (
                                <div className="h-8 w-8 mx-auto rounded-full border-4 border-[#384860]/20 border-t-[#384860] animate-spin" />
                            ) : slots.length === 0 ? (
                                <p className="text-[#384860]/70 text-sm">{strings.rescheduleFlow.noSlots}</p>
                            ) : (
                                <div className="flex flex-wrap gap-2 justify-start">
                                    {slots.map(s => (
                                        <button
                                            key={s.start_time}
                                            onClick={() => setSelectedSlot(s)}
                                            className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${selectedSlot?.start_time === s.start_time
                                                ? 'bg-[#384860] text-white border-[#384860]'
                                                : 'bg-white text-[#384860] border-[#384860]/20 hover:border-[#384860]/60'}`}
                                        >
                                            {s.start_time}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    {selectedDay && selectedSlot && (
                        <div className="mt-6 bg-[#93cbb6]/10 border border-[#93cbb6]/40 rounded-xl p-4 text-left">
                            <p className="text-sm text-[#384860]/70">{strings.rescheduleFlow.confirmTitle}</p>
                            <p className="text-[#384860] font-semibold">
                                {formatDay(selectedDay, appointment.language)}, {selectedSlot.start_time}
                            </p>
                        </div>
                    )}

                    <div className="mt-6 flex flex-col gap-3">
                        <button
                            onClick={doReschedule}
                            disabled={busy || !selectedDay || !selectedSlot}
                            className="w-full rounded-xl bg-[#384860] hover:bg-[#2c3a4e] disabled:opacity-40 text-white font-semibold py-3 transition-colors"
                        >
                            {busy ? strings.rescheduleFlow.submitting : strings.rescheduleFlow.confirm}
                        </button>
                        <button
                            onClick={() => { setView('details'); setFlash(null); }}
                            disabled={busy}
                            className="w-full rounded-xl border border-[#384860]/20 text-[#384860] font-semibold py-3 hover:bg-[#384860]/5 transition-colors"
                        >
                            {strings.actions.back}
                        </button>
                    </div>
                </>
            );
        }

        // view === 'details'
        const blockedNotice = (() => {
            if (policy.canCancel || policy.canReschedule) return null;
            if (policy.reason === 'status' || policy.reason === 'past' || policy.reason === 'disabled') return strings.notActionable;
            if (policy.reschedulesRemaining === 0 && policy.hoursUntil >= policy.cancelMinHours) return null;
            return strings.tooLate.replace('{{hours}}', String(policy.cancelMinHours));
        })();

        return (
            <>
                <h1 className="text-2xl font-semibold text-[#384860] mb-6">{strings.pageTitle.split('—')[0].trim()}</h1>
                {flash && <p className="mb-4 text-sm text-red-600">{flash}</p>}
                {detailsCard}

                {(policy.canCancel || policy.canReschedule) ? (
                    <div className="mt-6 flex flex-col gap-3">
                        {policy.canReschedule && (
                            <button
                                onClick={openReschedule}
                                className="w-full rounded-xl bg-[#384860] hover:bg-[#2c3a4e] text-white font-semibold py-3 transition-colors"
                            >
                                {strings.actions.reschedule}
                            </button>
                        )}
                        {!policy.canReschedule && policy.reschedulesRemaining === 0 && (
                            <p className="text-sm text-[#384860]/60">{strings.maxReschedules}</p>
                        )}
                        {policy.canCancel && (
                            <button
                                onClick={() => setView('confirmCancel')}
                                className="w-full rounded-xl border border-red-200 text-red-600 font-semibold py-3 hover:bg-red-50 transition-colors"
                            >
                                {strings.actions.cancel}
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="mt-6 text-[#384860]/70 text-sm space-y-1">
                        {blockedNotice && <p>{blockedNotice}</p>}
                        <p>{strings.callUs}</p>
                    </div>
                )}
            </>
        );
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-[#f9f9f7] to-white px-6 py-10 text-center">
            <div className="max-w-md w-full">
                <div className="mb-6 text-[#384860] font-semibold tracking-wide">SeaDent Mallorca</div>
                {renderBody()}
            </div>
        </div>
    );
};

export default ManageAppointmentPage;
